<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text class="orangehrm-main-title" tag="h6">
        {{ $t('payroll.edit_payslips') }}
      </oxd-text>
      <oxd-alert
        v-if="payslips.errorMessage"
        :show="true"
        type="warn"
        :message="payslips.errorMessage"
        class="payroll-error-alert"
      ></oxd-alert>
      <oxd-divider />
      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <employee-autocomplete
                v-model="payslips.employee"
                :rules="rules.employee"
                disabled
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.status"
                :label="$t('general.status')"
                capitalize
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.generatedAt"
                :label="$t('payroll.generated_at')"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.emailSentAt"
                :label="$t('payroll.email_sent_at')"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row class="attendance-row">
          <div
            class="title-payslip-section title-payslip-section--with-link attendance-section-title"
          >
            <oxd-text tag="p" class="title-payslip-section__label">
              <template v-if="attendanceFromDate && attendanceToDate">
                Attendance {{ attendanceFromDate }} – {{ attendanceToDate }}
                <span
                  v-if="attendanceRecords.length"
                  class="attendance-record-count"
                >
                  · {{ attendanceRecords.length }} records
                </span>
              </template>
              <template v-else>Attendance</template>
            </oxd-text>
            <oxd-text
              v-if="employeeEmpNumber"
              tag="p"
              class="title-payslip-section__link"
              @click="onOpenEmployeeAttendance"
            >
              {{ $t('attendance.employee_attendance_records') }}
            </oxd-text>
          </div>
          <div class="orangehrm-container">
            <payslip-attendance-breakdown-table
              :items="attendanceRecords"
              :loading="isLoading"
            />
            <oxd-text tag="p" class="attendance-legend-note">
              Note: WF = Work Fraction, LF = Leave Fraction, HF = Holiday
              Fraction, PF = Payable Fraction, PH = Public Holiday, UNPAID =
              Unpaid Leave, leave codes = configured type abbreviation (or
              initials from the leave type name), OW = Outside Work Week.
            </oxd-text>
          </div>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-text tag="p" class="title-payslip-section">
            Work Day Parameters
          </oxd-text>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.scheduledWorkDays"
                label="Scheduled Work Days"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.publicHolidays"
                label="Holidays"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.totalCalculationDays"
                label="Maximum Payable Days"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-text tag="p" class="title-payslip-section">
            Employee Payable Days
          </oxd-text>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.workingDays"
                label="Work Days"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.absenceDays"
                label="Unpaid Leave / Absence Days"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.leaveFraction"
                label="Paid Leave Days"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.holidayFraction"
                label="Public Holidays"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.payableDays"
                label="Total Payable Days"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-text tag="p" class="title-payslip-section">
            Employee Attendance Details
          </oxd-text>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.workingDays"
                label="Attended Days (WF)"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.fullDayAttendance"
                label="Full-Day Attendance"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.halfDayAttendance"
                label="Half-Day Attendance"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.lateMinutesTotal"
                label="Late Minutes (Total)"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.earlyDepartures"
                label="Early Departures"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.annualLeaveDays"
                label="Annual Leave Days"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.sickLeaveDays"
                label="Sick Leave Days"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.permissionLeaveDays"
                label="Permission Leave Days"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <div class="title-payslip-section title-payslip-section--with-link">
            <oxd-text tag="p" class="title-payslip-section__label">
              Salary Calculation Details
            </oxd-text>
            <oxd-text
              v-if="employeeEmpNumber"
              tag="p"
              class="title-payslip-section__link"
              @click="onOpenSalaryComponents"
            >
              {{ $t('pim.assigned_salary_components') }}
            </oxd-text>
          </div>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.basicSalaryMonthly"
                label="Basic Salary"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.basicSalary"
                label="Daily Rate"
                :hint="dailyRateFormulaHint"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.grossSalary"
                label="Gross Salary"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.taxableIncome"
                label="Taxable Income"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.ptkpStatus"
                label="PTKP Status"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.nonNpwpStatus"
                label="Tax Status"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="payslips.calculation.taxPercentage"
                label="Tax Percentage"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-text tag="p" class="title-payslip-section">
            Payslip Earnings
          </oxd-text>
          <oxd-grid
            v-for="item in earningItems"
            :key="item.id"
            :cols="4"
            class="orangehrm-full-width-grid"
          >
            <oxd-grid-item>
              <div class="wrap-name">
                <oxd-icon-button
                  name="trash"
                  class="icon-delete"
                  @click="onDeleteItem(item)"
                />
                <oxd-input-field
                  v-model="item.itemName"
                  style="text-transform: capitalize"
                  :label="$t('general.name')"
                  required
                />
              </div>
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="item.itemType"
                type="select"
                :label="$t('general.type')"
                :options="typeOptions"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="item.amount"
                style="text-transform: capitalize"
                :label="$t('general.amount')"
                required
                @blur="item.amount = formatAmountForDisplay(item.amount)"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="item.description"
                style="text-transform: capitalize"
                :label="$t('general.description')"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
          <div class="payslips-action payslips-action-bottom">
            <oxd-icon-button name="plus" @click="onAddEarningItem" />
          </div>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-text tag="p" class="title-payslip-section">
            Payslip Deductions
          </oxd-text>
          <oxd-grid
            v-for="item in deductionItems"
            :key="item.id"
            :cols="4"
            class="orangehrm-full-width-grid"
          >
            <oxd-grid-item>
              <div class="wrap-name">
                <oxd-icon-button
                  name="trash"
                  class="icon-delete"
                  @click="onDeleteItem(item)"
                />
                <oxd-input-field
                  v-model="item.itemName"
                  style="text-transform: capitalize"
                  :label="$t('general.name')"
                  required
                />
              </div>
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="item.itemType"
                type="select"
                :label="$t('general.type')"
                :options="typeOptions"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="item.amount"
                style="text-transform: capitalize"
                :label="$t('general.amount')"
                required
                @blur="item.amount = formatAmountForDisplay(item.amount)"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="item.description"
                style="text-transform: capitalize"
                :label="$t('general.description')"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
          <div class="payslips-action payslips-action-bottom">
            <oxd-icon-button name="plus" @click="onAddDeductionItem" />
          </div>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-text tag="p" class="title-payslip-section">
            Payslip Totals
          </oxd-text>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <payslip-readonly-field
                :value="totalEarningsDisplay"
                label="Total Earnings"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="totalDeductionDisplay"
                label="Total Deduction"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <payslip-readonly-field
                :value="netSalaryDisplay"
                label="Net Salary"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-divider />
        <oxd-form-actions>
          <required-text />
          <div class="orangehrm-button-row">
            <oxd-button
              display-type="ghost"
              :label="$t('general.cancel')"
              @click="onCancel"
            />
            <submit-button />
          </div>
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {formatEmployeeFullName} from '@/core/util/helper/employeeDisplayName';
import {navigate} from '@/core/util/helper/navigation';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import {APIService} from '@/core/util/services/api.service';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {OxdAlert} from '@ohrm/oxd';
import moment from 'moment/moment';
import PayslipAttendanceBreakdownTable from '@/orangehrmPayrollPlugin/components/PayslipAttendanceBreakdownTable.vue';
import PayslipReadonlyField from '@/orangehrmPayrollPlugin/components/PayslipReadonlyField.vue';
import {DAILY_RATE_FORMULA_HINT} from '@/orangehrmPayrollPlugin/util/payrollFormulas';

const payslipsModel = {
  employee: null,
  status: '',
  generatedAt: null,
  emailSentAt: '',
  errorMessage: '',
  calculation: {
    basicSalaryMonthly: '',
    basicSalary: '',
    grossSalary: '',
    scheduledWorkDays: '',
    publicHolidays: '',
    leaveFraction: '',
    holidayFraction: '',
    totalCalculationDays: '',
    ptkpStatus: '',
    nonNpwpStatus: '',
    taxPercentage: '',
    taxableIncome: '',
    payableDays: '',
    workingDays: '',
    fullDayAttendance: '',
    halfDayAttendance: '',
    lateArrivals: '',
    latePercentDeductionTotal: '',
    lateHalfDayDeductionTotal: '',
    lateMinutesTotal: '',
    earlyDepartures: '',
    absenceDays: '',
    annualLeaveDays: '',
    sickLeaveDays: '',
    permissionLeaveDays: '',
  },
  items: [],
};

export default {
  components: {
    'employee-autocomplete': EmployeeAutocomplete,
    'oxd-alert': OxdAlert,
    'payslip-attendance-breakdown-table': PayslipAttendanceBreakdownTable,
    'payslip-readonly-field': PayslipReadonlyField,
  },
  props: {
    payrollPayslipsId: {
      type: Number,
      required: true,
    },
    payrollRunId: {
      type: Number,
      required: true,
    },
  },
  setup() {
    const {userDateFormat} = useDateFormat();
    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/payroll/payslips`,
    );
    return {
      http,
      userDateFormat,
    };
  },
  data() {
    return {
      isLoading: false,
      payslips: {...payslipsModel},
      attendanceFromDate: '',
      attendanceToDate: '',
      attendanceRecords: [],
      lateMinutesByDate: {},
      workWeekConfig: null,
      holidayDateSet: new Set(),
      lateDeductionDetailParsed: false,
      halfDayCalcDateSet: new Set(),
      lateDetailByDate: {},
      statutoryDailyRateRaw: 0,
      leaveByDate: {},
      rules: {},
      typeOptions: [
        {id: 1, param: 'earning', label: this.$t('payroll.payslips_earning')},
        {
          id: 2,
          param: 'deduction',
          label: this.$t('payroll.payslips_deduction'),
        },
      ],
    };
  },
  computed: {
    dailyRateFormulaHint() {
      return DAILY_RATE_FORMULA_HINT;
    },
    employeeEmpNumber() {
      const id = this.payslips.employee?.id;
      return id !== undefined && id !== null && id !== '' ? Number(id) : null;
    },
    earningItems() {
      return (this.payslips.items || []).filter(
        (item) => item.itemType?.param === 'earning',
      );
    },
    deductionItems() {
      return (this.payslips.items || []).filter(
        (item) => item.itemType?.param === 'deduction',
      );
    },
    totalEarningsDisplay() {
      return this.formatAmountForDisplay(
        this.sumItemAmounts(this.earningItems),
      );
    },
    totalDeductionDisplay() {
      return this.formatAmountForDisplay(
        this.sumItemAmounts(this.deductionItems),
      );
    },
    netSalaryDisplay() {
      return this.formatAmountForDisplay(
        this.sumItemAmounts(this.earningItems) -
          this.sumItemAmounts(this.deductionItems),
      );
    },
  },
  created() {
    this.isLoading = true;
    const formatNumber = (value, fractionDigits = 2) => {
      const n = Number(value);
      if (!Number.isFinite(n)) {
        return '';
      }
      return n.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: fractionDigits,
      });
    };
    const formatTime = (time) => {
      if (!time) return '';
      const utcTime = moment.utc(time, 'YYYY-MM-DD HH:mm:ss.SSSSSS');
      const localTime = utcTime.clone().tz(moment.tz.guess());
      return localTime.format('YYYY-MM-DD hh:mm A');
    };
    this.http
      .get(this.payrollPayslipsId)
      .then(async (response) => {
        const {data} = response.data;
        this.payslips.employee = data.employee
          ? {
              id: data.employee.empNumber,
              label: formatEmployeeFullName(data.employee),
            }
          : null;
        this.payslips.status = data.status;
        this.payslips.generatedAt = formatTime(data.generatedAt?.date);
        this.payslips.emailSentAt = formatTime(data.emailSentAt?.date);
        this.payslips.errorMessage = data.notes || data.emailLastError || '';
        const items = data.payslipItems || [];
        this.payslips.items = items.map((item) => ({
          ...item,
          amount: formatNumber(item.amount, 2),
          itemType:
            this.typeOptions.find((opt) => opt.param === item.itemType) || null,
        }));
        const basicSalaryItem = items.find(
          (item) =>
            item.itemType === 'earning' &&
            typeof item.itemName === 'string' &&
            item.itemName.toLowerCase().includes('basic'),
        );
        const summary = data.attendanceSummary || {};
        const attendedDays = Number(summary.attendanceDays ?? 0);
        const absenceDays = Number(summary.absenceDays ?? 0);
        const annualLeaveDays = Number(summary.annualLeaveDays ?? 0);
        const sickLeaveDays = Number(summary.sickLeaveDays ?? 0);
        const permissionLeaveDays = Number(summary.permissionLeaveDays ?? 0);
        const scheduledWorkDays =
          attendedDays +
          absenceDays +
          annualLeaveDays +
          sickLeaveDays +
          permissionLeaveDays;
        const monthlyFromDescription = (() => {
          const desc = basicSalaryItem?.description;
          if (typeof desc !== 'string' || desc.length === 0) {
            return 0;
          }
          const match = desc.match(/MONTHLY_BASIS:([0-9]+(?:\.[0-9]+)?)/);
          return match ? Number(match[1]) : 0;
        })();
        const scheduledFromDescription = (() => {
          const desc = basicSalaryItem?.description;
          if (typeof desc !== 'string' || desc.length === 0) {
            return null;
          }
          const match = desc.match(/SCHEDULED_WORK_DAYS:([0-9]+(?:\.[0-9]+)?)/);
          return match ? Number(match[1]) : null;
        })();
        const basicNominal = Number(
          monthlyFromDescription || basicSalaryItem?.amount || 0,
        );
        const statutoryDailyRate =
          basicNominal > 0 ? (basicNominal / 173) * 8 : 0;
        this.statutoryDailyRateRaw = statutoryDailyRate;
        this.payslips.calculation.basicSalaryMonthly =
          basicNominal > 0 ? formatNumber(basicNominal, 2) : '';
        this.payslips.calculation.basicSalary =
          basicNominal > 0 ? formatNumber(statutoryDailyRate, 2) : '';
        this.payslips.calculation.grossSalary = '';
        this.payslips.calculation.scheduledWorkDays = formatNumber(
          scheduledFromDescription ?? scheduledWorkDays,
          2,
        );
        this.payslips.calculation.publicHolidays = '';
        this.payslips.calculation.leaveFraction = '';
        this.payslips.calculation.holidayFraction = '';
        this.payslips.calculation.ptkpStatus = '';
        this.payslips.calculation.nonNpwpStatus = '';
        this.payslips.calculation.taxPercentage = '';
        this.payslips.calculation.taxableIncome = '';
        // Keep this API-sourced (from attendance breakdown summary) to avoid
        // duplicating calculation logic in the UI.
        this.payslips.calculation.totalCalculationDays = '';
        this.payslips.calculation.workingDays =
          summary.attendanceDays !== undefined &&
          summary.attendanceDays !== null
            ? formatNumber(summary.attendanceDays, 2)
            : '';
        const lateDetailRaw = summary.lateDeductionDetailJson;
        let halfDayLateCount = 0;
        let lateMinutesTotal = 0;
        let latePercentDeductionSum = 0;
        let lateHalfDayDeductionSum = 0;
        const lateMinutesByDate = {};
        this.lateDeductionDetailParsed = false;
        this.halfDayCalcDateSet = new Set();
        this.lateDetailByDate = {};
        if (typeof lateDetailRaw === 'string' && lateDetailRaw.trim() !== '') {
          try {
            const parsed = JSON.parse(lateDetailRaw);
            if (Array.isArray(parsed)) {
              this.lateDeductionDetailParsed = true;
              parsed.forEach((row) => {
                const date =
                  typeof row?.date === 'string' && row.date ? row.date : null;
                if (date) {
                  this.lateDetailByDate[date] = row;
                }
                if (row?.classification === 'half_day') {
                  halfDayLateCount += 1;
                  if (date) {
                    this.halfDayCalcDateSet.add(date);
                  }
                  const halfAmt = Number(row?.deductionAmount);
                  if (Number.isFinite(halfAmt)) {
                    lateHalfDayDeductionSum += halfAmt;
                  }
                } else if (row?.classification === 'percent') {
                  const pctAmt = Number(row?.deductionAmount);
                  if (Number.isFinite(pctAmt)) {
                    latePercentDeductionSum += pctAmt;
                  }
                } else if (row?.classification === 'day_fraction') {
                  const dayAmt = Number(row?.deductionAmount);
                  if (Number.isFinite(dayAmt)) {
                    latePercentDeductionSum += dayAmt;
                  }
                }
                const mins = Number(row?.minutesAfterShiftStart ?? 0);
                if (date && Number.isFinite(mins)) {
                  lateMinutesByDate[date] = mins;
                  lateMinutesTotal += mins;
                }
              });
            }
          } catch (e) {
            halfDayLateCount = 0;
            lateMinutesTotal = 0;
            latePercentDeductionSum = 0;
            lateHalfDayDeductionSum = 0;
            this.lateDeductionDetailParsed = false;
            this.halfDayCalcDateSet = new Set();
            this.lateDetailByDate = {};
          }
        }
        this.lateMinutesByDate = lateMinutesByDate;
        const attendedDaysRaw = Number(summary.attendanceDays ?? 0);
        const halfDayAttendanceRaw = Math.max(0, halfDayLateCount * 0.5);
        const fullDayAttendanceRaw = Math.max(
          0,
          attendedDaysRaw - halfDayAttendanceRaw,
        );
        this.payslips.calculation.fullDayAttendance = formatNumber(
          fullDayAttendanceRaw,
          2,
        );
        this.payslips.calculation.halfDayAttendance = formatNumber(
          halfDayAttendanceRaw,
          2,
        );

        this.payslips.calculation.lateArrivals =
          summary.lateArrivals !== undefined && summary.lateArrivals !== null
            ? formatNumber(summary.lateArrivals, 2)
            : '';
        this.payslips.calculation.latePercentDeductionTotal =
          this.lateDeductionDetailParsed && latePercentDeductionSum > 0
            ? formatNumber(latePercentDeductionSum, 2)
            : this.lateDeductionDetailParsed
            ? formatNumber(0, 2)
            : '';
        this.payslips.calculation.lateHalfDayDeductionTotal =
          this.lateDeductionDetailParsed && lateHalfDayDeductionSum > 0
            ? formatNumber(lateHalfDayDeductionSum, 2)
            : this.lateDeductionDetailParsed
            ? formatNumber(0, 2)
            : '';
        this.payslips.calculation.lateMinutesTotal = formatNumber(
          lateMinutesTotal,
          0,
        );
        this.payslips.calculation.earlyDepartures =
          summary.earlyDepartures !== undefined &&
          summary.earlyDepartures !== null
            ? formatNumber(summary.earlyDepartures, 2)
            : '';
        this.payslips.calculation.absenceDays =
          summary.absenceDays !== undefined && summary.absenceDays !== null
            ? formatNumber(summary.absenceDays, 2)
            : '';
        this.payslips.calculation.annualLeaveDays =
          summary.annualLeaveDays !== undefined &&
          summary.annualLeaveDays !== null
            ? formatNumber(summary.annualLeaveDays, 2)
            : '';
        this.payslips.calculation.sickLeaveDays =
          summary.sickLeaveDays !== undefined && summary.sickLeaveDays !== null
            ? formatNumber(summary.sickLeaveDays, 2)
            : '';
        this.payslips.calculation.permissionLeaveDays =
          summary.permissionLeaveDays !== undefined &&
          summary.permissionLeaveDays !== null
            ? formatNumber(summary.permissionLeaveDays, 2)
            : '';

        const runHttp = new APIService(
          window.appGlobal.baseUrl,
          '/api/v2/payroll/runs',
        );
        const runRes = await runHttp.get(this.payrollRunId);
        const runData = runRes?.data?.data ?? {};
        const fromDate = this.extractDateValue(runData.periodStartDate);
        const toDate = this.extractDateValue(runData.periodEndDate);
        this.attendanceFromDate = fromDate;
        this.attendanceToDate = toDate;
        if (fromDate && toDate) {
          await this.fetchAttendanceRecordsForPeriod();
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  },
  methods: {
    extractDateValue(value) {
      if (!value) {
        return '';
      }
      if (typeof value === 'string') {
        return value.split(' ')[0];
      }
      if (typeof value === 'object' && value.date) {
        return String(value.date).split(' ')[0];
      }
      return '';
    },
    async fetchAttendanceRecordsForPeriod() {
      const breakdownHttp = new APIService(
        window.appGlobal.baseUrl,
        `/api/v2/payroll/payslips/${this.payrollPayslipsId}/attendance-breakdown`,
      );
      const res = await breakdownHttp.getAll();
      const rows = res?.data?.data?.rows ?? [];
      const totals = res?.data?.data?.totals ?? {};
      const summary = res?.data?.data?.summary ?? null;
      const normalizedRows = (Array.isArray(rows) ? rows : [])
        .map((row, idx) => ({
          id: row.date || `r-${idx}`,
          dayNote: row.dayDescription || '',
          workDate: row.date || '',
          dayName: this.getDayNameShort(row.date || ''),
          punchInTime: this.formatTime12Hour(row.punchInTime || ''),
          punchOutTime: this.formatTime12Hour(row.punchOutTime || ''),
          dayType: row.dayType || '',
          attendanceFraction: this.formatDayFraction(row.attendanceFraction),
          leaveFraction: this.formatDayFraction(row.leaveFraction),
          holidayFraction: this.formatDayFraction(row.holidayFraction),
          payableFraction: this.formatDayFraction(row.payableFraction),
          lateTier: this.formatLateDeductionDisplay(row),
          lateMinutes: this.formatWholeNumberOrZero(row.lateMinutes),
          latePermission: row.latePermission || '-',
          earlyLeavePermission: row.earlyLeavePermission || '-',
          duration: this.formatAmountOrZero(row.durationHours),
          punchInNote: row.punchInNote || '',
          punchOutNote: row.punchOutNote || '',
        }))
        .sort((a, b) => String(a.workDate).localeCompare(String(b.workDate)));
      const totalRow = {
        id: 'total-row',
        dayNote: '',
        workDate: '',
        dayName: '',
        punchInTime: '',
        punchOutTime: '',
        dayType: 'TOTAL',
        attendanceFraction: this.formatDayFraction(totals.attendanceFraction),
        leaveFraction: this.formatDayFraction(totals.leaveFraction),
        holidayFraction: this.formatDayFraction(totals.holidayFraction),
        payableFraction: this.formatDayFraction(totals.payableFraction),
        lateTier: this.formatLateDeductionTotalDisplay(totals),
        lateMinutes: this.formatWholeNumberOrZero(totals.lateMinutes),
        latePermission: '',
        earlyLeavePermission: '',
        duration: this.formatAmountOrZero(totals.durationHours),
        punchInNote: '',
        punchOutNote: '',
      };
      this.attendanceRecords = [...normalizedRows, totalRow];
      this.applyCalculationSummary(summary);
      if (typeof totals.payableFraction === 'number') {
        this.payslips.calculation.payableDays = this.formatCalculationValue(
          totals.payableFraction,
        );
      }
    },
    applyCalculationSummary(summary) {
      if (!summary || typeof summary !== 'object') {
        return;
      }
      this.payslips.calculation.basicSalaryMonthly =
        this.formatCalculationValue(summary.basicSalaryMonthly);
      this.payslips.calculation.basicSalary = this.formatCalculationValue(
        summary.basicSalaryDailyRate,
      );
      this.payslips.calculation.grossSalary = this.formatCalculationValue(
        summary.grossSalary,
      );
      this.payslips.calculation.scheduledWorkDays = this.formatCalculationValue(
        summary.scheduledWorkDays,
      );
      this.payslips.calculation.publicHolidays = this.formatCalculationValue(
        summary.publicHolidays ?? summary.holidayFraction,
      );
      this.payslips.calculation.leaveFraction = this.formatCalculationValue(
        summary.leaveFraction,
      );
      this.payslips.calculation.holidayFraction = this.formatCalculationValue(
        summary.holidayFraction ?? summary.publicHolidays,
      );
      this.payslips.calculation.totalCalculationDays =
        this.formatCalculationValue(summary.totalCalculationDays);
      this.payslips.calculation.workingDays = this.formatCalculationValue(
        summary.attendedDays,
      );
      this.payslips.calculation.fullDayAttendance = this.formatCalculationValue(
        summary.fullDayAttendance,
      );
      this.payslips.calculation.halfDayAttendance = this.formatCalculationValue(
        summary.halfDayAttendance,
      );
      this.payslips.calculation.lateArrivals = this.formatCalculationValue(
        summary.lateArrivals,
      );
      this.payslips.calculation.latePercentDeductionTotal =
        this.formatCalculationValue(summary.latePercentDeductionTotal);
      this.payslips.calculation.lateHalfDayDeductionTotal =
        this.formatCalculationValue(summary.lateHalfDayDeductionTotal);
      this.payslips.calculation.lateMinutesTotal = this.formatWholeNumberOrZero(
        summary.lateMinutesTotal,
      );
      this.payslips.calculation.earlyDepartures = this.formatCalculationValue(
        summary.earlyDepartures,
      );
      this.payslips.calculation.absenceDays = this.formatCalculationValue(
        summary.absenceDays,
      );
      this.payslips.calculation.annualLeaveDays = this.formatCalculationValue(
        summary.annualLeaveDays,
      );
      this.payslips.calculation.sickLeaveDays = this.formatCalculationValue(
        summary.sickLeaveDays,
      );
      this.payslips.calculation.permissionLeaveDays =
        this.formatCalculationValue(summary.permissionLeaveDays);
      this.payslips.calculation.ptkpStatus =
        typeof summary.ptkpStatus === 'string' ? summary.ptkpStatus : '';
      this.payslips.calculation.nonNpwpStatus =
        typeof summary.nonNpwpStatus === 'string' ? summary.nonNpwpStatus : '';
      const taxPct = Number(summary.taxPercentage);
      this.payslips.calculation.taxPercentage = Number.isFinite(taxPct)
        ? `${this.formatCalculationValue(taxPct)}%`
        : '';
      this.payslips.calculation.taxableIncome = this.formatCalculationValue(
        summary.taxableIncome,
      );
    },
    formatCalculationValue(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) {
        return '';
      }
      return n.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    formatLateDeductionAmount(amt) {
      const n = Number(amt);
      if (!Number.isFinite(n)) {
        return '';
      }
      return n.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    formatAmountOrZero(amt) {
      const n = Number(amt);
      if (!Number.isFinite(n)) {
        return '0';
      }
      return this.formatLateDeductionAmount(n) || '0';
    },
    formatLateDeductionDisplay(row) {
      if (!row || typeof row !== 'object') {
        return '0';
      }
      const classification = String(row.lateDeductionClassification || '');
      const amt = Number(row.lateDeduction);
      const hasAmt = Number.isFinite(amt) && Math.abs(amt) >= 0.005;

      if (
        classification === 'half_day' ||
        classification === 'permission_waived' ||
        !hasAmt
      ) {
        return '0';
      }
      if (classification === 'day_fraction') {
        const frac = Number(row.lateDeductionDayFraction);
        const fracText =
          Number.isFinite(frac) && frac > 0
            ? `${this.formatCalculationValue(frac)} day(s)`
            : '';
        const amtText = this.formatAmountByBrowserLocale(amt);
        return fracText ? `${fracText} ${amtText}` : amtText;
      }
      if (classification === 'amount') {
        return this.formatAmountByBrowserLocale(amt);
      }
      if (classification === 'percent') {
        return this.formatLateDeductionWithPercent(amt, row.matchedBandPercent);
      }
      const pct = Number(row.matchedBandPercent);
      if (Number.isFinite(pct) && pct > 0) {
        return this.formatLateDeductionWithPercent(amt, pct);
      }
      return this.formatAmountByBrowserLocale(amt);
    },
    formatLateDeductionTotalDisplay(totals) {
      if (!totals || typeof totals !== 'object') {
        return '0';
      }
      const pctTotal = Number(totals.latePercentPointsTotal) || 0;
      const pctAmount = Number(totals.latePercentDeduction) || 0;
      if (pctTotal > 0 && pctAmount > 0) {
        return this.formatLateDeductionWithPercent(pctAmount, pctTotal);
      }
      return this.formatAmountOrZero(Number(totals.lateDeduction) || 0);
    },
    formatLatePercentBandLabel(percent) {
      const n = Number(percent);
      if (!Number.isFinite(n) || n <= 0) {
        return '';
      }
      return `${n.toFixed(2).replace(/\.?0+$/, '')}%`;
    },
    formatLateDeductionWithPercent(amount, percent) {
      const amt = Number(amount);
      const pct = Number(percent);
      const hasAmt = Number.isFinite(amt) && Math.abs(amt) >= 0.005;
      const hasPct = Number.isFinite(pct) && pct > 0;
      if (!hasAmt && !hasPct) {
        return '0';
      }
      if (hasAmt && !hasPct) {
        return this.formatAmountByBrowserLocale(amt);
      }
      const pctLabel = this.formatLatePercentBandLabel(pct);
      const pctText = pctLabel ? `(${pctLabel})` : '';
      const amtText = this.formatAmountByBrowserLocale(amt);
      return `${pctText} ${amtText}`;
    },
    formatAmountByBrowserLocale(amt) {
      const n = Number(amt);
      if (!Number.isFinite(n)) {
        return '0,00';
      }
      return n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatWholeNumberOrZero(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) {
        return '0';
      }
      return Math.round(n).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
    formatDayFraction(value) {
      const n = Number(value);
      if (!Number.isFinite(n) || n <= 0) {
        return '0';
      }
      return n.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    getDayNameShort(dateStr) {
      if (!dateStr || typeof dateStr !== 'string') {
        return '';
      }
      const d = moment(dateStr, 'YYYY-MM-DD', true);
      if (!d.isValid()) {
        return '';
      }
      return d.format('ddd').toUpperCase();
    },
    formatTime12Hour(time) {
      if (!time || typeof time !== 'string') {
        return '';
      }
      const parsed = moment(
        time,
        [
          'YYYY-MM-DD HH:mm:ss',
          'YYYY-MM-DD H:mm:ss',
          'YYYY-MM-DD HH:mm',
          'YYYY-MM-DD H:mm',
          'HH:mm:ss',
          'H:mm:ss',
          'HH:mm',
          'H:mm',
          'hh:mm:ss A',
          'h:mm:ss A',
          'hh:mm A',
          'h:mm A',
        ],
        true,
      );
      if (parsed.isValid()) {
        return parsed.format('hh:mm A');
      }
      const fallback = moment(time);
      return fallback.isValid() ? fallback.format('hh:mm A') : time;
    },
    formatAmountForDisplay(value) {
      const normalized = this.normalizeAmountForApi(value);
      const n = Number(normalized);
      if (!Number.isFinite(n)) {
        return value;
      }
      return n.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    sumItemAmounts(items) {
      return (items || []).reduce((sum, item) => {
        const amount = Number(this.normalizeAmountForApi(item.amount));
        return sum + (Number.isFinite(amount) ? amount : 0);
      }, 0);
    },
    normalizeAmountForApi(value) {
      if (value === null || value === undefined) {
        return '';
      }
      return String(value).replace(/,/g, '');
    },
    addItemWithType(typeParam) {
      const typeOption =
        this.typeOptions.find((opt) => opt.param === typeParam) || null;
      const newItem = {
        id: null,
        itemName: '',
        itemType: typeOption,
        amount: '',
        description: '',
      };
      this.payslips.items.push(newItem);
    },

    onAddEarningItem() {
      this.addItemWithType('earning');
    },

    onAddDeductionItem() {
      this.addItemWithType('deduction');
    },

    onDeleteItem(item) {
      const index = this.payslips.items.findIndex((i) => i.id === item.id);
      if (index !== -1) {
        this.payslips.items.splice(index, 1);
      }
    },
    onCancel() {
      navigate(`/payroll/viewPayrollPayslips/${this.payrollRunId}`);
    },
    onOpenSalaryComponents() {
      if (!this.employeeEmpNumber) {
        return;
      }
      navigate('/pim/viewSalaryList/empNumber/{empNumber}', {
        empNumber: this.employeeEmpNumber,
      });
    },
    onOpenEmployeeAttendance() {
      if (!this.employeeEmpNumber) {
        return;
      }
      const query = {
        employeeId: this.employeeEmpNumber,
      };
      if (this.attendanceFromDate) {
        query.fromDate = this.attendanceFromDate;
      }
      if (this.attendanceToDate) {
        query.toDate = this.attendanceToDate;
      }
      navigate('/attendance/viewAttendanceRecord', undefined, query);
    },
    onSave() {
      const http = new APIService(
        window.appGlobal.baseUrl,
        `/api/v2/payroll/payslips/${this.payrollPayslipsId}/items`,
      );
      this.isLoading = true;
      const payload = {
        items: this.payslips.items.map((item) => ({
          id: item.id === null ? null : item.id,
          itemName: item.itemName,
          itemType: item.itemType?.param,
          amount: this.normalizeAmountForApi(item.amount),
          description: item.description,
        })),
      };
      http
        .update(null, payload)
        .then(() => {
          return this.$toast.updateSuccess();
        })
        .then(() => {
          this.onCancel();
        })
        .catch((response) => {
          return this.$toast.warn({
            title: this.$t('general.warning'),
            message: response?.data.error.message,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
<style lang="scss" scoped>
.wrap-name {
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 10px;
}

.icon-delete {
  font-size: 12px;
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  padding: 0;
}

.payslips-action {
  margin-bottom: 10px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.payslips-action-bottom {
  justify-content: flex-end;
  margin-top: 0.5rem;
}

.title-payslip-section {
  width: 100%;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #d9dee9;
}

.title-payslip-section--with-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.title-payslip-section__label {
  margin: 0;
}

.title-payslip-section__link {
  margin: 0;
  color: #ff7b1d;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
  text-decoration: underline;
}

.title-payslip-section__link:hover {
  color: #e56a10;
}

.orangehrm-button-row {
  gap: 0.4rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  max-width: 120px;
  margin-left: 60px;

  ::v-deep(.oxd-button--medium) {
    width: 100%;
  }

  @include oxd-respond-to('md') {
    margin-left: unset;
    max-width: unset;
    ::v-deep(.oxd-button--medium) {
      width: unset;
    }
  }
}

.payroll-error-alert {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.attendance-section-title .attendance-record-count {
  color: #64748b;
  font-weight: 400;
}

.attendance-legend-note {
  margin-top: 0.5rem;
  color: #64748b;
  font-size: 0.85rem;
}

.attendance-row {
  margin-bottom: 1.5rem;
  min-width: 0;
}
</style>
